import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

const MAINTENANCE_REDIRECT_URL = 'https://getmoremath.com/unavailable/';
const MAINTENANCE_PATH = '/unavailable';
const REDIRECT_STARTED_AT_KEY = 'gmm-maintenance-redirect-started-at';
const REDIRECT_GUARD_MILLISECONDS = 30000;
const TRUSTED_MAINTENANCE_ORIGINS = [new URL(MAINTENANCE_REDIRECT_URL).origin];

let redirectStarted = false;

type MaintenanceRedirectCandidate = AxiosResponse | AxiosError | undefined;
type MaintenanceResponseHandler = (
  response: AxiosResponse
) => AxiosResponse | Promise<AxiosResponse>;

/**
 * Installs shared maintenance redirect handling on an Axios instance.
 * Runs the optional response handler only after a response is not maintenance.
 */
export const addMaintenanceRedirectInterceptor = (
  instance: AxiosInstance,
  onResponse?: MaintenanceResponseHandler
): void => {
  instance.interceptors.response.use(
    function axiosMaintenanceRedirectResponseSuccess(response) {
      if (redirectToMaintenancePageIfNeeded(response)) {
        return waitForMaintenanceNavigation();
      }

      return onResponse ? onResponse(response) : response;
    },
    function axiosMaintenanceRedirectResponseError(error: AxiosError) {
      if (redirectToMaintenancePageIfNeeded(error)) {
        return waitForMaintenanceNavigation();
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Handles an Axios result whose final browser URL is the known maintenance page
 * and returns whether maintenance navigation owns the user-visible outcome.
 */
export const redirectToMaintenancePageIfNeeded = (
  result?: MaintenanceRedirectCandidate
): boolean => {
  const maintenanceResponseUrl = getMaintenanceResponseUrl(result);

  if (!maintenanceResponseUrl) return false;

  return redirectToMaintenancePage(maintenanceResponseUrl);
};

/**
 * Handles a plain browser URL that resolved to the maintenance page.
 */
export const redirectToMaintenancePageUrlIfNeeded = (url?: string): boolean =>
  url && isTrustedMaintenancePageUrl(url)
    ? redirectToMaintenancePage(url)
    : false;

/**
 * Clears the redirect guard so tests can exercise fresh maintenance responses.
 */
export const resetMaintenanceRedirectGuardForTest = (): void => {
  redirectStarted = false;

  try {
    getSessionStorage()?.removeItem(REDIRECT_STARTED_AT_KEY);
  } catch {
    // Some browser storage implementations can reject access even after being returned.
  }
};

/**
 * Starts one guarded browser navigation when maintenance mode is detected.
 */
const redirectToMaintenancePage = (url: string): boolean => {
  if (typeof window === 'undefined') return false;
  if (isMaintenancePageUrl(window.location.href)) return true;
  if (isRedirectGuarded()) return true;

  redirectStarted = true;

  try {
    getSessionStorage()?.setItem(REDIRECT_STARTED_AT_KEY, `${Date.now()}`);
  } catch {
    // The in-memory flag still guards the current runtime when storage is unavailable.
  }

  try {
    window.location.replace(url);

    return true;
  } catch {
    redirectStarted = false;

    try {
      getSessionStorage()?.removeItem(REDIRECT_STARTED_AT_KEY);
    } catch {
      // The original Axios response or error can continue when navigation is blocked.
    }

    return false;
  }
};

/**
 * Keeps callers from rendering stale success or error UI while the browser leaves the app.
 */
const waitForMaintenanceNavigation = <T>(): Promise<T> =>
  new Promise<T>(() => {
    // Intentionally unresolved because the maintenance page replaces this runtime.
  });

/**
 * Prevents one tab from repeatedly redirecting when several requests finish during maintenance.
 */
const isRedirectGuarded = (): boolean => {
  if (redirectStarted) return true;

  const startedAt = getRedirectStartedAt();

  return (
    startedAt !== undefined &&
    Date.now() - startedAt < REDIRECT_GUARD_MILLISECONDS
  );
};

const getRedirectStartedAt = (): number | undefined => {
  try {
    const startedAt = Number(
      getSessionStorage()?.getItem(REDIRECT_STARTED_AT_KEY)
    );

    return Number.isFinite(startedAt) ? startedAt : undefined;
  } catch {
    return undefined;
  }
};

const getSessionStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

/**
 * Reads Axios' final browser URL and treats that URL as the only maintenance-mode signal.
 */
const getMaintenanceResponseUrl = (
  result?: MaintenanceRedirectCandidate
): string | undefined => {
  if (!result) return undefined;

  const responseUrl =
    getResponseUrl(getAxiosResponse(result)?.request) ||
    getResponseUrl(isAxiosError(result) ? result.request : undefined);

  return responseUrl && isTrustedMaintenancePageUrl(responseUrl)
    ? responseUrl
    : undefined;
};

const getAxiosResponse = (
  result: AxiosResponse | AxiosError
): AxiosResponse | undefined =>
  isAxiosError(result) ? result.response : result;

const isAxiosError = (
  result: AxiosResponse | AxiosError
): result is AxiosError => 'isAxiosError' in result && result.isAxiosError;

const getResponseUrl = (request: unknown): string | undefined =>
  (request as { responseURL?: string } | undefined)?.responseURL;

const isMaintenancePageUrl = (url: string): boolean => {
  const parsedUrl = parseUrl(url);

  return !!parsedUrl && hasMaintenancePath(parsedUrl);
};

/**
 * Allows the known public unavailable page plus same-origin unavailable pages from deployed envs.
 */
const isTrustedMaintenancePageUrl = (url: string): boolean => {
  const parsedUrl = parseUrl(url);

  if (!parsedUrl || !hasMaintenancePath(parsedUrl)) return false;

  return (
    parsedUrl.origin === getCurrentOrigin() ||
    TRUSTED_MAINTENANCE_ORIGINS.includes(parsedUrl.origin)
  );
};

const parseUrl = (url: string): URL | undefined => {
  try {
    return new URL(url, getUrlBase());
  } catch {
    return undefined;
  }
};

const getUrlBase = (): string =>
  typeof window === 'undefined'
    ? MAINTENANCE_REDIRECT_URL
    : window.location.href;

const getCurrentOrigin = (): string | undefined =>
  typeof window === 'undefined'
    ? undefined
    : parseUrl(window.location.href)?.origin;

const hasMaintenancePath = (url: URL): boolean =>
  normalizePath(url.pathname) === MAINTENANCE_PATH;

const normalizePath = (pathname: string): string =>
  pathname.replace(/\/+$/, '').toLowerCase() || '/';
